import { useCallback, useMemo, useRef, useState } from 'react'
import Page from '../components/Page'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined'
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DomainOutlinedIcon from '@mui/icons-material/DomainOutlined'
import GestureRoundedIcon from '@mui/icons-material/GestureRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import Map, { Layer, MapRef, Source, LayerProps } from 'react-map-gl'
import ConfigProvider from 'balkerne-core/config'
import axios from 'axios'
import { useSnackbar } from 'notistack'
import DrawControl from '../components/userManagement/DrawControl'
import type { AllGeoJSON } from '@turf/turf'
import { area as turfArea, bbox, centroid } from '@turf/turf'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import api from 'balkerne-core/api'
import { useMutation, useQueryClient } from 'react-query'
import { useHistory } from 'react-router-dom'
import siteMap from '../siteMap'
import { usePropertyTypes } from '../hooks/locations'
import { useCurrentUser } from '../hooks/users'
import LocalisedNumberTextField from '../components/LocalisedNumberTextField'
import { alpha } from '@mui/material/styles'
import { FeatureGate } from 'balkerne-core/roles'

type GeometryMode = 'building' | 'draw'

type FormValues = {
  name: string
  customer_reference_number: string | null
  type_id: string | null
  text: string | null
  country: string | null
  town: string | null
  street: string | null
  postcode: string | null
  structure_value: number | null
  contents_value: number | null
  revenue: number | null
}

const emptyToNullString = (value: unknown, originalValue: unknown) => {
  if (typeof originalValue === 'string' && originalValue.trim() === '') return null
  return value
}

const emptyToNullNumber = (value: unknown, originalValue: unknown) => {
  if (originalValue === '' || originalValue === null || originalValue === undefined) return null
  return value
}

const schema = yup
  .object({
    name: yup.string().trim().required('Name is required'),
    customer_reference_number: yup.string().nullable().transform(emptyToNullString),
    type_id: yup.string().nullable().transform(emptyToNullString),
    text: yup.string().nullable().transform(emptyToNullString),
    country: yup.string().nullable().transform(emptyToNullString),
    town: yup.string().nullable().transform(emptyToNullString),
    street: yup.string().nullable().transform(emptyToNullString),
    postcode: yup.string().nullable().transform(emptyToNullString),
    structure_value: yup
      .number()
      .nullable()
      .transform(emptyToNullNumber)
      .typeError('Structure value must be a number')
      .min(0, 'Structure value must be at least 0'),
    contents_value: yup
      .number()
      .nullable()
      .transform(emptyToNullNumber)
      .typeError('Contents value must be a number')
      .min(0, 'Contents value must be at least 0'),
    revenue: yup
      .number()
      .nullable()
      .transform(emptyToNullNumber)
      .typeError('Revenue must be a number')
      .min(0, 'Revenue must be at least 0'),
  })
  .required()

type MapboxGeocodeFeature = {
  center?: [number, number]
  bbox?: [number, number, number, number]
  place_name?: string
  text?: string
  address?: string
  context?: Array<{ id: string; text: string }>
  place_type?: string[]
}

type AddressParts = {
  text?: string | null
  country?: string | null
  town?: string | null
  street?: string | null
  postcode?: string | null
}

const pickContext = (feature: MapboxGeocodeFeature | null | undefined, prefix: string) => {
  const ctx = feature?.context ?? []
  return ctx.find(c => (c?.id ?? '').startsWith(prefix))?.text
}

const mapboxFeatureToAddressParts = (feature: MapboxGeocodeFeature): AddressParts => {
  const formatted = feature.place_name ?? null
  const postcode = pickContext(feature, 'postcode.') ?? null
  const country = pickContext(feature, 'country.') ?? null

  const town =
    pickContext(feature, 'place.') ??
    pickContext(feature, 'locality.') ??
    pickContext(feature, 'district.') ??
    pickContext(feature, 'region.') ??
    null

  const isAddress = (feature.place_type ?? []).includes('address')
  const street = isAddress
    ? `${feature.address ?? ''} ${feature.text ?? ''}`.trim() || null
    : // fallback: first comma-separated segment of place_name
      feature.place_name?.split(',')?.[0]?.trim() || null

  return {
    text: formatted,
    country,
    town,
    street,
    postcode,
  }
}

const geometryToFeatureCollection = (
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> => {
  if (geometry.type === 'Polygon') {
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry }],
    }
  }
  // MultiPolygon -> split into Polygon features (DrawControl + backend already handle FeatureCollections)
  return {
    type: 'FeatureCollection',
    features: geometry.coordinates.map(coords => ({
      type: 'Feature',
      properties: {},
      geometry: { type: 'Polygon', coordinates: coords },
    })),
  }
}

const BUILDINGS_SOURCE_ID = 'mapbox-streets'
const BUILDINGS_SOURCE_URL = 'mapbox://mapbox.mapbox-streets-v8'
const BUILDINGS_FILL_LAYER_ID = 'buildings-fill'

const buildingsFillLayer: LayerProps = {
  id: BUILDINGS_FILL_LAYER_ID,
  type: 'fill',
  source: BUILDINGS_SOURCE_ID,
  'source-layer': 'building',
  minzoom: 15,
  paint: {
    'fill-color': '#00bcd4',
    'fill-opacity': 0.12,
  },
}

const buildingsLineLayer: LayerProps = {
  id: 'buildings-line',
  type: 'line',
  source: BUILDINGS_SOURCE_ID,
  'source-layer': 'building',
  minzoom: 15,
  paint: {
    'line-color': '#00bcd4',
    'line-opacity': 0.5,
    'line-width': 1,
  },
}

const SELECTED_GEOMETRY_COLOR = '#00e676'

const selectedFillLayer: LayerProps = {
  id: 'selected-building-fill',
  type: 'fill',
  source: 'selected-building',
  paint: { 'fill-color': SELECTED_GEOMETRY_COLOR, 'fill-opacity': 0.25 },
}

const selectedLineLayer: LayerProps = {
  id: 'selected-building-line',
  type: 'line',
  source: 'selected-building',
  paint: { 'line-color': SELECTED_GEOMETRY_COLOR, 'line-width': 2, 'line-opacity': 0.9 },
}

const hoveredLineLayer: LayerProps = {
  id: 'hovered-building-line',
  type: 'line',
  source: 'hovered-building',
  paint: { 'line-color': '#ffd54f', 'line-width': 2, 'line-opacity': 0.9 },
}

const drawStyles: any[] = [
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: {
      'fill-color': SELECTED_GEOMETRY_COLOR,
      'fill-outline-color': SELECTED_GEOMETRY_COLOR,
      'fill-opacity': 0.25,
    },
  },
  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': SELECTED_GEOMETRY_COLOR,
      'fill-outline-color': SELECTED_GEOMETRY_COLOR,
      'fill-opacity': 0.25,
    },
  },
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 3,
      'circle-color': SELECTED_GEOMETRY_COLOR,
    },
  },
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': SELECTED_GEOMETRY_COLOR,
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': SELECTED_GEOMETRY_COLOR,
      'line-dasharray': [0.2, 2],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-line-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': SELECTED_GEOMETRY_COLOR,
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': SELECTED_GEOMETRY_COLOR,
      'line-dasharray': [0.2, 2],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#fff',
    },
  },
  {
    id: 'gl-draw-polygon-and-line-vertex-inactive',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: {
      'circle-radius': 3,
      'circle-color': SELECTED_GEOMETRY_COLOR,
    },
  },
  {
    id: 'gl-draw-point-point-stroke-inactive',
    type: 'circle',
    filter: [
      'all',
      ['==', 'active', 'false'],
      ['==', '$type', 'Point'],
      ['==', 'meta', 'feature'],
      ['!=', 'mode', 'static'],
    ],
    paint: {
      'circle-radius': 5,
      'circle-opacity': 1,
      'circle-color': '#fff',
    },
  },
  {
    id: 'gl-draw-point-inactive',
    type: 'circle',
    filter: [
      'all',
      ['==', 'active', 'false'],
      ['==', '$type', 'Point'],
      ['==', 'meta', 'feature'],
      ['!=', 'mode', 'static'],
    ],
    paint: {
      'circle-radius': 3,
      'circle-color': SELECTED_GEOMETRY_COLOR,
    },
  },
  {
    id: 'gl-draw-point-stroke-active',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'active', 'true'], ['!=', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 7,
      'circle-color': '#fff',
    },
  },
  {
    id: 'gl-draw-point-active',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['!=', 'meta', 'midpoint'], ['==', 'active', 'true']],
    paint: {
      'circle-radius': 5,
      'circle-color': SELECTED_GEOMETRY_COLOR,
    },
  },
  {
    id: 'gl-draw-polygon-fill-static',
    type: 'fill',
    filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': '#404040',
      'fill-outline-color': '#404040',
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'gl-draw-polygon-stroke-static',
    type: 'line',
    filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#404040',
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-line-static',
    type: 'line',
    filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'LineString']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': '#404040',
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-point-static',
    type: 'circle',
    filter: ['all', ['==', 'mode', 'static'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 5,
      'circle-color': '#404040',
    },
  },
]

async function mapboxForwardGeocode(address: string): Promise<MapboxGeocodeFeature[]> {
  const token = ConfigProvider.mapboxPublicKey
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`
  const res = await axios.get(url, { params: { access_token: token, limit: 5 } })
  return res.data?.features ?? []
}

async function createPropertyType(label: string) {
  return api.post(`/locations/types`, { data: { label } })
}

const AddProperty = () => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { data: propertyTypes, isLoading: propertyTypesLoading, refetch: refetchTypes } = usePropertyTypes()
  const { currencyISO, currencySymbol } = useCurrentUser()

  const [typeCreateOpen, setTypeCreateOpen] = useState(false)
  const [newTypeLabel, setNewTypeLabel] = useState('')
  const [geometryMode, setGeometryMode] = useState<GeometryMode>('building')
  const [mapStyleMode, setMapStyleMode] = useState<'satellite' | 'streets'>('satellite')
  const [mapHelpOpen, setMapHelpOpen] = useState(false)
  const [addressQuery, setAddressQuery] = useState('')
  const [geocoding, setGeocoding] = useState(false)

  const mapRef = useRef<MapRef | null>(null)
  const [selectedBuilding, setSelectedBuilding] = useState<GeoJSON.Feature<
    GeoJSON.Polygon | GeoJSON.MultiPolygon,
    GeoJSON.GeoJsonProperties
  > | null>(null)
  const [hoveredBuilding, setHoveredBuilding] = useState<GeoJSON.Feature<
    GeoJSON.Polygon | GeoJSON.MultiPolygon,
    GeoJSON.GeoJsonProperties
  > | null>(null)

  const [drawControlKey, setDrawControlKey] = useState(0)
  const [drawFeatures, setDrawFeatures] = useState<Record<string, GeoJSON.Feature>>({})

  const drawnFeatureCollection = useMemo(() => {
    const features = Object.values(drawFeatures)
    if (features.length === 0) return null
    return { type: 'FeatureCollection', features } as GeoJSON.FeatureCollection
  }, [drawFeatures])

  const selectedBuildingFeatureCollection = useMemo(() => {
    if (!selectedBuilding) return null
    return geometryToFeatureCollection(selectedBuilding.geometry)
  }, [selectedBuilding])

  const activeGeometry = useMemo(() => {
    if (geometryMode === 'draw') return drawnFeatureCollection
    return selectedBuildingFeatureCollection
  }, [drawnFeatureCollection, geometryMode, selectedBuildingFeatureCollection])

  const hasGeometry = (activeGeometry?.features?.length ?? 0) > 0

  const {
    control,
    register,
    setValue,
    handleSubmit: formSubmit,
    formState: { errors, isSubmitting, isValid },
    watch,
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onBlur',
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      customer_reference_number: null,
      type_id: null,
      text: null,
      country: null,
      town: null,
      street: null,
      postcode: null,
      structure_value: null,
      contents_value: null,
      revenue: null,
    },
  })
  const addressStreet = watch('street')
  const addressTown = watch('town')
  const addressPostcode = watch('postcode')
  const addressCountry = watch('country')
  const structureValue = watch('structure_value')
  const contentsValue = watch('contents_value')
  const revenueValue = watch('revenue')

  const defaultType = useMemo(() => {
    return (propertyTypes ?? []).find((t: any) => (t?.label ?? '').trim().toLowerCase() === 'default') ?? null
  }, [propertyTypes])
  const defaultTypeId = (defaultType as any)?.id ?? null
  const propertyTypeOptions = useMemo(() => {
    const types: any[] = propertyTypes ?? []
    if (!defaultTypeId) return types
    return [defaultType, ...types.filter(t => t?.id !== defaultTypeId)].filter(Boolean)
  }, [defaultType, defaultTypeId, propertyTypes])

  const addressSummary = [addressStreet, addressTown, addressPostcode, addressCountry].filter(Boolean).join(', ')

  const financialSummary = useMemo(() => {
    const fmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 })
    const parts: string[] = []
    if (typeof structureValue === 'number') parts.push(`Structure: ${currencySymbol}${fmt.format(structureValue)}`)
    if (typeof contentsValue === 'number') parts.push(`Contents: ${currencySymbol}${fmt.format(contentsValue)}`)
    if (typeof revenueValue === 'number') parts.push(`Revenue: ${currencySymbol}${fmt.format(revenueValue)}`)
    return parts.join(' · ') || '—'
  }, [contentsValue, currencySymbol, revenueValue, structureValue])

  const createTypeMutation = useMutation((label: string) => createPropertyType(label), {
    onSuccess: async res => {
      const created = (res as any)?.data
      await refetchTypes()
      if (created?.id) {
        setValue('type_id', created.id, { shouldDirty: true, shouldValidate: true })
      }
      enqueueSnackbar('Property type created', { variant: 'success' })
      setTypeCreateOpen(false)
    },
    onError: err => {
      console.error(err)
      enqueueSnackbar('Error creating property type', { variant: 'error' })
    },
  })

  const createPropertyMutation = useMutation(
    async (payload: any) => {
      return await api.post('/locations/create', { properties: [payload] })
    },
    {
      onSuccess: res => {
        const data = (res as any)?.data ?? {}
        const failureCount = Number(data?.failure_count ?? 0)
        if (failureCount > 0) {
          const msg = data?.failures?.[0]?.message ?? 'Error creating property'
          enqueueSnackbar(msg, { variant: 'error' })
          return
        }

        const createdId = data?.property_ids?.[0]
        enqueueSnackbar('Property created', { variant: 'success' })
        queryClient.invalidateQueries(['/locations'])
        if (Number.isInteger(createdId)) {
          history.push(siteMap.Property.getPath(createdId))
        } else {
          history.push(siteMap.Organisation.path)
        }
      },
      onError: err => {
        console.error(err)
        const message = (err as any)?.response?.data?.message ?? 'Error creating property'
        enqueueSnackbar(message, { variant: 'error' })
      },
    },
  )

  const focusOnGeometry = useCallback((geo: AllGeoJSON) => {
    const map = mapRef.current?.getMap?.()
    if (!map) return
    try {
      const [minX, minY, maxX, maxY] = bbox(geo)
      map.fitBounds(
        [
          [minX, minY],
          [maxX, maxY],
        ],
        { padding: 60, duration: 500, maxZoom: 17 },
      )
    } catch (err) {
      console.warn(err)
      const center = centroid(geo).geometry.coordinates
      map.flyTo({ center: [center[0], center[1]], zoom: 18, duration: 500 })
    }
  }, [])

  const clearSelectedBuilding = () => setSelectedBuilding(null)

  const clearDrawnGeometry = () => {
    setDrawFeatures({})
    setDrawControlKey(k => k + 1)
  }

  const applyAddressParts = useCallback(
    (parts: AddressParts) => {
      if (parts.text !== undefined) setValue('text', parts.text ?? null, { shouldDirty: true })
      if (parts.country !== undefined) setValue('country', parts.country ?? null, { shouldDirty: true })
      if (parts.town !== undefined) setValue('town', parts.town ?? null, { shouldDirty: true })
      if (parts.street !== undefined) setValue('street', parts.street ?? null, { shouldDirty: true })
      if (parts.postcode !== undefined) setValue('postcode', parts.postcode ?? null, { shouldDirty: true })
    },
    [setValue],
  )

  const setMode = useCallback(
    (mode: GeometryMode) => {
      if (mode === 'draw') {
        // Transfer the selected building footprint into a drawable polygon.
        if (selectedBuilding) {
          const geom = selectedBuilding.geometry
          const featureGeometry =
            geom.type === 'MultiPolygon'
              ? (() => {
                  if (!geom.coordinates?.length) return null
                  let bestCoords = geom.coordinates[0]
                  let bestArea = -Infinity
                  for (const coords of geom.coordinates) {
                    const candidate = {
                      type: 'Feature',
                      properties: {},
                      geometry: { type: 'Polygon', coordinates: coords },
                    } as any
                    const a = turfArea(candidate)
                    if (a > bestArea) {
                      bestArea = a
                      bestCoords = coords
                    }
                  }
                  return { type: 'Polygon', coordinates: bestCoords } as GeoJSON.Polygon
                })()
              : (geom as GeoJSON.Polygon)

          if (featureGeometry) {
            setDrawFeatures({
              __imported__: { type: 'Feature', properties: {}, geometry: featureGeometry } as any,
            })
            setDrawControlKey(k => k + 1)
          }
        }
        clearSelectedBuilding()
        setHoveredBuilding(null)
      } else {
        // Reset draw geometry when switching back to Select mode.
        clearDrawnGeometry()
      }
      setGeometryMode(mode)
    },
    [clearDrawnGeometry, clearSelectedBuilding, selectedBuilding],
  )

  const trySelectBuildingAtLngLat = useCallback(
    (lng: number, lat: number) => {
      const map = mapRef.current?.getMap?.()
      if (!map) return
      map.once('idle', () => {
        try {
          const point = map.project([lng, lat])
          const hits: any[] = map.queryRenderedFeatures(point, { layers: [BUILDINGS_FILL_LAYER_ID] }) as any
          const hit = hits?.find(f => f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'MultiPolygon')
          if (!hit?.geometry) return
          setSelectedBuilding({
            type: 'Feature',
            geometry: hit.geometry,
            properties: hit.properties ?? {},
          })
        } catch (err) {
          console.warn(err)
        }
      })
    },
    [setSelectedBuilding],
  )

  const handleSearch = useCallback(async () => {
    const query = addressQuery.trim()
    if (!query) return
    setGeocoding(true)
    try {
      const features = await mapboxForwardGeocode(query)
      if (!features || features.length === 0) {
        enqueueSnackbar('No matching addresses found', { variant: 'warning' })
        return
      }
      const best = features[0]
      const center = best.center
      if (best) applyAddressParts(mapboxFeatureToAddressParts(best))
      if (mapRef.current) {
        const bbox = best?.bbox
        const centerFromBbox =
          bbox && bbox.length === 4 ? ([(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2] as [number, number]) : null
        const viewCenter = center ?? centerFromBbox

        if (bbox && bbox.length === 4) {
          mapRef.current.getMap?.()?.fitBounds(
            [
              [bbox[0], bbox[1]],
              [bbox[2], bbox[3]],
            ],
            { padding: 60, duration: 600, maxZoom: 18 },
          )
          console.debug("<AddProperty> Fitting map to geocoding result's bbox:", bbox)
        } else if (center) {
          mapRef.current.flyTo({ center, zoom: 18, duration: 600 })
          console.debug('<AddProperty> Flying map to geocoding result center:', center)
        }

        if (viewCenter && geometryMode === 'building') {
          trySelectBuildingAtLngLat(viewCenter[0], viewCenter[1])
        }
      }
    } catch (err) {
      console.error(err)
      enqueueSnackbar('Error occured when searching for address.', { variant: 'error' })
    } finally {
      setGeocoding(false)
    }
  }, [addressQuery, applyAddressParts, enqueueSnackbar, geometryMode, trySelectBuildingAtLngLat])

  const onDrawUpdate = useCallback((e: any) => {
    const updated = (e.features ?? []).find((f: any) => f?.id && f?.geometry?.type === 'Polygon')
    if (!updated) return
    setDrawFeatures({ [updated.id]: updated })
  }, [])

  // Limit custom geometry to a single polygon. Creating a new polygon replaces the existing one.
  const onDrawCreate = useCallback((e: any) => {
    const created = (e.features ?? []).find((f: any) => f?.id && f?.geometry?.type === 'Polygon')
    if (!created) return
    setDrawFeatures({ [created.id]: created })
    setDrawControlKey(k => k + 1)
  }, [])

  const onDrawDelete = useCallback((e: any) => {
    setDrawFeatures(curr => {
      const next = { ...curr }
      for (const f of e.features ?? []) {
        if (f?.id) delete next[f.id]
      }
      return next
    })
  }, [])

  const initDrawFeatures = useCallback((features: { [key: string]: GeoJSON.Feature }) => {
    setDrawFeatures({ ...features })
  }, [])

  const initialViewState = useMemo(() => {
    return { latitude: 51.522, longitude: -0.226, zoom: 9 }
  }, [])

  const handleSubmit = async (data: FormValues) => {
    if (!hasGeometry) {
      enqueueSnackbar('Please select a building or draw a geometry before creating the property.', {
        variant: 'warning',
      })
      return
    }
    const payload: any = {
      name: data.name?.trim(),
      customer_reference_number: data.customer_reference_number ?? null,
      geometry: activeGeometry ?? undefined,
      text: [data.street, data.town, data.postcode, data.country].filter(Boolean).join(', ').trim() || null,
      country: data.country ?? null,
      town: data.town ?? null,
      street: data.street ?? null,
      postcode: data.postcode ?? null,
      structure_value: data.structure_value ?? null,
      contents_value: data.contents_value ?? null,
      revenue: data.revenue ?? null,
    }
    const resolvedTypeId = data.type_id ?? defaultTypeId
    if (resolvedTypeId) payload.type_id = resolvedTypeId

    await createPropertyMutation.mutateAsync(payload)
  }

  return (
    <Page title="New Property" size="lg" back>
      <Card elevation={0}>
        <CardContent>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Name"
                    fullWidth
                    {...register('name')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Reference"
                    fullWidth
                    {...register('customer_reference_number')}
                    error={!!errors.customer_reference_number}
                    helperText={errors.customer_reference_number?.message}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <Controller
                      name="type_id"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          select
                          label="Type"
                          value={field.value ?? defaultTypeId ?? ''}
                          onChange={e => {
                            const value = e.target.value
                            if (value === '__create__') {
                              setNewTypeLabel('')
                              setTypeCreateOpen(true)
                              return
                            }
                            field.onChange(value)
                          }}
                          disabled={propertyTypesLoading}>
                          {propertyTypeOptions.map((t: any) => (
                            <MenuItem key={t.id} value={t.id}>
                              {t.label}
                            </MenuItem>
                          ))}
                          <MenuItem value="__create__">
                            <ListItemIcon>
                              <AddRoundedIcon fontSize="small" />
                            </ListItemIcon>
                            Create new type…
                          </MenuItem>
                        </TextField>
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Boundary</Typography>
                <Tooltip title="How to use the map">
                  <IconButton size="small" aria-label="Map help" onClick={() => setMapHelpOpen(true)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <TextField
                  label="Search address"
                  placeholder="Start typing an address…"
                  value={addressQuery}
                  onChange={e => setAddressQuery(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleSearch()
                    }
                  }}
                  InputLabelProps={{ shrink: Boolean(addressQuery) }}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Clear search">
                          <span>
                            <IconButton
                              onClick={() => setAddressQuery('')}
                              disabled={geocoding || !addressQuery.trim()}
                              edge="end"
                              size="small">
                              <ClearRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Search">
                          <span>
                            <IconButton onClick={handleSearch} disabled={geocoding || !addressQuery.trim()} edge="end">
                              <SearchOutlinedIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Box mt={2} />

              <Box
                sx={{
                  height: 560,
                  width: '100%',
                  borderRadius: 1,
                  overflow: 'hidden',
                  position: 'relative',
                  '& .mapboxgl-ctrl-draw': { display: 'none' },
                }}>
                <Map
                  ref={mapRef}
                  mapStyle={
                    mapStyleMode === 'satellite'
                      ? 'mapbox://styles/mapbox/satellite-streets-v12'
                      : 'mapbox://styles/mapbox/streets-v12'
                  }
                  initialViewState={initialViewState}
                  mapboxAccessToken={ConfigProvider.mapboxPublicKey}
                  interactiveLayerIds={geometryMode === 'building' ? [BUILDINGS_FILL_LAYER_ID] : []}
                  onMouseMove={e => {
                    if (geometryMode !== 'building') return
                    const f: any = e.features?.[0]
                    if (!f?.geometry || (f.geometry.type !== 'Polygon' && f.geometry.type !== 'MultiPolygon')) {
                      setHoveredBuilding(null)
                      return
                    }
                    setHoveredBuilding({ type: 'Feature', geometry: f.geometry, properties: f.properties ?? {} })
                  }}
                  onMouseLeave={() => setHoveredBuilding(null)}
                  onClick={e => {
                    if (geometryMode !== 'building') return
                    const f: any = e.features?.[0]
                    if (!f?.geometry || (f.geometry.type !== 'Polygon' && f.geometry.type !== 'MultiPolygon')) return
                    setSelectedBuilding({ type: 'Feature', geometry: f.geometry, properties: f.properties ?? {} })
                    setHoveredBuilding(null)
                  }}>
                  {geometryMode === 'building' && (
                    <Source id={BUILDINGS_SOURCE_ID} type="vector" url={BUILDINGS_SOURCE_URL}>
                      <Layer {...buildingsFillLayer} />
                      <Layer {...buildingsLineLayer} />
                    </Source>
                  )}

                  {geometryMode === 'draw' && (
                    <DrawControl
                      key={drawControlKey}
                      position="top-left"
                      displayControlsDefault={false}
                      controls={{}}
                      styles={drawStyles}
                      defaultMode={
                        drawnFeatureCollection?.features?.length ? ('simple_select' as any) : ('draw_polygon' as any)
                      }
                      onCreate={onDrawCreate}
                      onUpdate={onDrawUpdate}
                      onDelete={onDrawDelete}
                      initGeometry={drawnFeatureCollection ?? undefined}
                      initFeatures={initDrawFeatures}
                    />
                  )}

                  {geometryMode === 'building' && hoveredBuilding && (
                    <Source id="hovered-building" type="geojson" data={hoveredBuilding}>
                      <Layer {...hoveredLineLayer} />
                    </Source>
                  )}

                  {geometryMode === 'building' && selectedBuilding && (
                    <Source id="selected-building" type="geojson" data={selectedBuilding}>
                      <Layer {...selectedFillLayer} />
                      <Layer {...selectedLineLayer} />
                    </Source>
                  )}
                </Map>

                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 2,
                    pointerEvents: 'none',
                  }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      pointerEvents: 'auto',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      p: 0.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(4px)',
                    }}>
                    <Tooltip title={geometryMode === 'building' ? 'Building mode (selected)' : 'Building mode'}>
                      <IconButton
                        size="small"
                        aria-label="Building mode"
                        aria-pressed={geometryMode === 'building'}
                        onClick={() => setMode('building')}
                        sx={theme => {
                          const selected = geometryMode === 'building'
                          return {
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: selected ? alpha(theme.palette.primary.main, 0.35) : 'transparent',
                            bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                            color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
                            '&:hover': {
                              bgcolor: selected
                                ? alpha(theme.palette.primary.main, 0.18)
                                : alpha(theme.palette.text.primary, 0.04),
                            },
                          }
                        }}>
                        <DomainOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={geometryMode === 'draw' ? 'Draw mode (selected)' : 'Draw mode'}>
                      <IconButton
                        size="small"
                        aria-label="Draw mode"
                        aria-pressed={geometryMode === 'draw'}
                        onClick={() => setMode('draw')}
                        sx={theme => {
                          const selected = geometryMode === 'draw'
                          return {
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: selected ? alpha(theme.palette.primary.main, 0.35) : 'transparent',
                            bgcolor: selected ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                            color: selected ? theme.palette.primary.main : theme.palette.text.secondary,
                            '&:hover': {
                              bgcolor: selected
                                ? alpha(theme.palette.primary.main, 0.18)
                                : alpha(theme.palette.text.primary, 0.04),
                            },
                          }
                        }}>
                        <GestureRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: 1, height: 22, bgcolor: 'divider', mx: 0.5 }} />
                    <Tooltip title={mapStyleMode === 'satellite' ? 'Switch to map view' : 'Switch to satellite view'}>
                      <IconButton
                        size="small"
                        aria-label={mapStyleMode === 'satellite' ? 'Switch to map view' : 'Switch to satellite view'}
                        onClick={() => setMapStyleMode(m => (m === 'satellite' ? 'streets' : 'satellite'))}>
                        {mapStyleMode === 'satellite' ? (
                          <MapOutlinedIcon fontSize="small" />
                        ) : (
                          <SatelliteAltOutlinedIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ width: 1, height: 22, bgcolor: 'divider', mx: 0.5 }} />
                    <Tooltip title="Focus on geometry">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!activeGeometry}
                          onClick={() => focusOnGeometry(activeGeometry as any)}>
                          <MyLocationRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Clear geometry">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={geometryMode === 'building' ? !selectedBuilding : !drawnFeatureCollection}
                          onClick={() => {
                            if (geometryMode === 'building') clearSelectedBuilding()
                            else clearDrawnGeometry()
                          }}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Paper>
                </Box>

                <Box sx={{ position: 'absolute', top: 52, left: 8, zIndex: 2, pointerEvents: 'none' }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      px: 1,
                      py: 0.75,
                      borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.92)',
                      backdropFilter: 'blur(4px)',
                    }}>
                    <Typography variant="caption" color="text.secondary">
                      <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {geometryMode === 'building' ? 'Select' : 'Draw'}
                      </Box>
                      {': '}
                      {geometryMode === 'building'
                        ? 'Hover and click a building footprint.'
                        : 'Click to add points, then click the first point to finish.'}
                    </Typography>
                  </Paper>
                </Box>

                {!hasGeometry ? (
                  <Box sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 2, pointerEvents: 'none' }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.92)',
                        borderColor: theme => alpha(theme.palette.error.main, 0.35),
                        backdropFilter: 'blur(4px)',
                      }}>
                      <Typography variant="caption" color="error">
                        Select or draw a boundary to continue
                      </Typography>
                    </Paper>
                  </Box>
                ) : (
                  <Box sx={{ position: 'absolute', bottom: 8, left: 8, zIndex: 2, pointerEvents: 'none' }}>
                    <Paper
                      variant="outlined"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: theme => theme.palette.success.main,
                        borderColor: theme => theme.palette.success.dark,
                        backdropFilter: 'blur(4px)',
                      }}>
                      <Typography
                        variant="caption"
                        sx={{ color: theme => theme.palette.getContrastText(theme.palette.success.main) }}>
                        Geometry selected
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Box>

              <Box mt={2} />

              <Accordion
                defaultExpanded={false}
                variant="outlined"
                sx={{ bgcolor: 'grey.50', borderStyle: 'dashed', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0, flex: '1 1 auto' }}>
                      <Typography variant="subtitle1" color="text.secondary">
                        Address
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520 }}>
                        {addressSummary || '—'}
                      </Typography>
                    </Stack>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Address line"
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(addressStreet) }}
                        {...register('street')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Town / City"
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(addressTown) }}
                        {...register('town')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Postcode"
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(addressPostcode) }}
                        {...register('postcode')}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Country"
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(addressCountry) }}
                        {...register('country')}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Paper>
            <FeatureGate feature="projections">
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Accordion
                  defaultExpanded={false}
                  variant="outlined"
                  sx={{ bgcolor: 'grey.50', borderStyle: 'dashed' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ minWidth: 0, flex: '1 1 auto' }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          Exposure
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 520 }}>
                          {financialSummary}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        Amounts in {currencyISO} ({currencySymbol})
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Controller
                          name="structure_value"
                          control={control}
                          render={({ field }) => (
                            <LocalisedNumberTextField
                              name={field.name}
                              label="Structure value"
                              fullWidth
                              value={field.value}
                              onValueChange={field.onChange}
                              onBlur={field.onBlur}
                              inputRef={field.ref}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                              }}
                              error={!!errors.structure_value}
                              helperText={errors.structure_value?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Controller
                          name="contents_value"
                          control={control}
                          render={({ field }) => (
                            <LocalisedNumberTextField
                              name={field.name}
                              label="Contents value"
                              fullWidth
                              value={field.value}
                              onValueChange={field.onChange}
                              onBlur={field.onBlur}
                              inputRef={field.ref}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                              }}
                              error={!!errors.contents_value}
                              helperText={errors.contents_value?.message}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Controller
                          name="revenue"
                          control={control}
                          render={({ field }) => (
                            <LocalisedNumberTextField
                              name={field.name}
                              label="Revenue"
                              fullWidth
                              value={field.value}
                              onValueChange={field.onChange}
                              onBlur={field.onBlur}
                              inputRef={field.ref}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                              }}
                              error={!!errors.revenue}
                              helperText={errors.revenue?.message}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Paper>
            </FeatureGate>
          </Stack>
        </CardContent>
      </Card>

      <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between">
          <Typography
            variant="caption"
            sx={{
              color: !hasGeometry ? 'error.main' : isValid ? 'success.main' : 'text.secondary',
              flex: '1 1 auto',
            }}>
            {!hasGeometry
              ? 'Select a building or draw a boundary'
              : isValid
              ? 'Ready to submit'
              : 'Fill in required fields'}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button variant="outlined" onClick={() => history.push(siteMap.Organisation.path)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={formSubmit(handleSubmit)}
              disabled={!isValid || !hasGeometry || isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Create property type dialog */}
      <Dialog open={typeCreateOpen} onClose={() => setTypeCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New property type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Label"
            fullWidth
            value={newTypeLabel}
            onChange={e => setNewTypeLabel(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                const label = newTypeLabel.trim()
                if (label) createTypeMutation.mutate(label)
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeCreateOpen(false)} disabled={createTypeMutation.isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!newTypeLabel.trim() || createTypeMutation.isLoading}
            onClick={() => createTypeMutation.mutate(newTypeLabel.trim())}>
            {createTypeMutation.isLoading ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map help dialog */}
      <Dialog open={mapHelpOpen} onClose={() => setMapHelpOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>How to use the map</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Pick a mode in the top-left corner of the map, then draw or select a property boundary before creating the
              property.
            </Typography>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Modes
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters alignItems="center">
                  <ListItemIcon sx={{ minWidth: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DomainOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Building"
                    secondary="Hover and click a building footprint to select it."
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem disableGutters alignItems="center">
                  <ListItemIcon sx={{ minWidth: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <GestureRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Draw"
                    secondary="Click to add points, then click the first point to finish."
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              </List>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Editing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In 'Draw' mode you can fine-tune a boundary by dragging vertices or adding new ones inbetween existing
                points.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you select a building footprint first, switching to 'Draw' mode will allow you to edit it.
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Helpful controls
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters alignItems="center">
                  <ListItemIcon sx={{ minWidth: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Clear geometry"
                    secondary="Use the trash icon to clear the current boundary."
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem disableGutters alignItems="center">
                  <ListItemIcon sx={{ minWidth: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MyLocationRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Focus on geometry"
                    secondary="Zoom to the current boundary."
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
                <ListItem disableGutters alignItems="center">
                  <ListItemIcon sx={{ minWidth: 34, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <MapOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Map / satellite"
                    secondary="Switch between street view (labels) and satellite imagery."
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                    secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  />
                </ListItem>
              </List>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Page>
  )
}

export default AddProperty
